import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import Select from '@/components/primitives/Select.jsx';
import Textarea from '@/components/primitives/Textarea.jsx';
import Dialog from '@/components/patterns/Dialog.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import AUTOMATION_KINDS from '../lib/AUTOMATION_KINDS.js';
import askPieveAboutAutomation from '../lib/askPieveAboutAutomation.js';

const KIND_ITEMS = Object.values(AUTOMATION_KINDS).map((kind) => ({ value: kind.kind, label: kind.startsLabel }));

/**
 * Asks Pieve to build a new automation, or to change an existing one when `automation` is given.
 * Sends the request to the right conversation and opens it, where Pieve shows what it is doing.
 */
const AskPieveDialog = ({ open, onOpenChange, automation = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [kind, setKind] = useState('schedules');
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const isChange = Boolean(automation);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    const result = await dispatch(askPieveAboutAutomation({ kind: isChange ? automation.kind : kind, threadId: automation?.threadId, prompt }));
    setSending(false);

    if (!result.success) {
      setError(result.message);
      return;
    }

    toastManager.add({ title: 'Sent to Pieve', description: isChange ? 'Pieve is working on the change.' : 'Pieve is setting it up.', type: 'success' });
    setPrompt('');
    onOpenChange(false);
    navigate(`/threads/${result.threadId}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={isChange ? `Change ${automation.name}` : 'New automation'} description={isChange ? 'Tell Pieve what to change. It updates the automation and tells you what it did.' : 'Describe the outcome. Pieve sets up the steps, asks for anything it needs, and turns it on.'}>
      <form className="automations-ask" onSubmit={handleSubmit}>
        {!isChange && <Select label="How should it start?" items={KIND_ITEMS} value={kind} onValueChange={setKind} />}
        <Textarea label={isChange ? 'What should change' : 'What should Pieve do'} rows={4} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder={isChange ? 'Run it every weekday at 9am instead.' : 'Every Monday at 8am, send me the deals that closed last week.'} error={error || undefined} required />
        <div className="automations-ask__footer">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={sending} disabled={!prompt.trim()}>
            Send to Pieve
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default AskPieveDialog;
